import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Trash2, Sparkles, Package, Loader2, Layers } from 'lucide-react';
import { useDealLineItems, useCreateDealLineItem, useUpdateDealLineItem, useDeleteDealLineItem, useProducts, usePricingRules, calculatePriceWithRules } from '@/hooks/useProducts';
import { useProductBundles, calculateBundleSavings } from '@/hooks/useProductBundles';
import { useAIAssistant } from '@/hooks/useAIAssistant';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { QuickBundleCreateDialog } from './QuickBundleCreateDialog';
import { AIBundleRecommendations } from './AIBundleRecommendations';
import { SmartBundleSuggestions } from './SmartBundleSuggestions';

interface DealLineItemsProps {
  dealId: string;
  dealTitle: string;
  accountName?: string;
  accountId?: string;
  dealAmount?: number | null;
}

function formatCurrency(amount: number, currency: string = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function DealLineItems({ dealId, dealTitle, accountName, accountId, dealAmount }: DealLineItemsProps) {
  const { data: lineItems, isLoading: itemsLoading, refetch } = useDealLineItems(dealId);
  const { data: products } = useProducts();
  const { data: bundles } = useProductBundles();
  const { data: allRules } = usePricingRules();
  const createLineItem = useCreateDealLineItem();
  const updateLineItem = useUpdateDealLineItem();
  const deleteLineItem = useDeleteDealLineItem();
  const { chat, isLoading: aiLoading } = useAIAssistant();

  const [showForm, setShowForm] = useState(false);
  const [addMode, setAddMode] = useState<'product' | 'bundle'>('product');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedBundleId, setSelectedBundleId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [discountPercent, setDiscountPercent] = useState('0');
  const [isAddingBundle, setIsAddingBundle] = useState(false);

  const selectedProduct = products?.find(p => p.id === selectedProductId);
  const selectedBundle = bundles?.find(b => b.id === selectedBundleId);
  const productRules = allRules?.filter(r => r.product_id === selectedProductId) || [];

  const handleAddItem = async () => {
    const qty = parseInt(quantity) || 1;
    let unitPrice = parseFloat(customPrice) || selectedProduct?.base_price || 0;
    let discount = parseFloat(discountPercent) || 0;

    // Apply pricing rules if using a product
    if (selectedProduct && productRules.length > 0) {
      const { finalPrice } = calculatePriceWithRules(selectedProduct.base_price, qty, productRules);
      unitPrice = finalPrice / qty;
    }

    const discountAmount = (unitPrice * qty) * (discount / 100);
    const totalPrice = (unitPrice * qty) - discountAmount;

    await createLineItem.mutateAsync({
      deal_id: dealId,
      product_id: selectedProductId || null,
      name: customName || selectedProduct?.name || 'Custom Item',
      description: selectedProduct?.description || null,
      quantity: qty,
      unit_price: unitPrice,
      discount_percent: discount,
      discount_amount: discountAmount,
      total_price: totalPrice,
      ai_suggested_price: null,
      ai_price_reasoning: null,
      metadata: {},
    });

    resetForm();
  };

  const handleAddBundle = async () => {
    if (!selectedBundle || !selectedBundle.items?.length) {
      toast.error('No items in this bundle');
      return;
    }

    setIsAddingBundle(true);
    
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .single();

      if (!profile?.tenant_id) throw new Error('No tenant found');

      // Calculate bundle savings
      const savings = calculateBundleSavings(selectedBundle);
      const itemCount = selectedBundle.items.length;
      const pricePerItem = selectedBundle.bundle_price / itemCount;

      // Create line items for each product in the bundle
      const bundleItems = selectedBundle.items.map(item => {
        const product = item.product;
        const originalPrice = (product?.base_price || 0) * item.quantity;
        const discountedPrice = (pricePerItem / item.quantity) * item.quantity;
        const discountAmount = originalPrice - discountedPrice;
        const discountPct = originalPrice > 0 ? (discountAmount / originalPrice) * 100 : 0;

        return {
          deal_id: dealId,
          tenant_id: profile.tenant_id,
          bundle_id: selectedBundle.id,
          product_id: item.product_id,
          name: `${product?.name || 'Unknown'} (${selectedBundle.name})`,
          description: product?.description || null,
          quantity: item.quantity,
          unit_price: product?.base_price || 0,
          discount_percent: discountPct,
          discount_amount: discountAmount,
          total_price: discountedPrice,
          ai_suggested_price: null,
          ai_price_reasoning: null,
          metadata: { bundle_name: selectedBundle.name },
        };
      });

      // Insert all bundle items
      const { error } = await supabase
        .from('deal_line_items')
        .insert(bundleItems);

      if (error) throw error;

      toast.success(`Added ${bundleItems.length} items from "${selectedBundle.name}" bundle`);
      refetch();
      resetForm();
    } catch (error: any) {
      toast.error('Failed to add bundle: ' + error.message);
    } finally {
      setIsAddingBundle(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setAddMode('product');
    setSelectedProductId('');
    setSelectedBundleId('');
    setQuantity('1');
    setCustomName('');
    setCustomPrice('');
    setDiscountPercent('0');
  };

  const handleDelete = async (itemId: string) => {
    await deleteLineItem.mutateAsync({ id: itemId, deal_id: dealId });
  };

  const handleAIPricing = async (item: typeof lineItems[0]) => {
    const prompt = `Analyze this deal line item and suggest optimal pricing:
    
Deal: ${dealTitle}
Account: ${accountName || 'Unknown'}
Product: ${item.name}
Current Unit Price: $${item.unit_price}
Quantity: ${item.quantity}
Current Total: $${item.total_price}

Consider:
1. Volume discounts for quantity ${item.quantity}
2. Competitive pricing
3. Deal size optimization
4. Upsell opportunities

Provide a JSON response with:
{
  "suggested_price": number,
  "discount_recommendation": number (percentage),
  "reasoning": "brief explanation"
}`;

    const response = await chat(prompt);
    
    if (response) {
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          
          await updateLineItem.mutateAsync({
            id: item.id,
            deal_id: dealId,
            ai_suggested_price: parsed.suggested_price,
            ai_price_reasoning: parsed.reasoning,
          });
          
          toast.success('AI pricing suggestion added');
        }
      } catch {
        toast.error('Could not parse AI suggestion');
      }
    }
  };

  const totalValue = lineItems?.reduce((sum, item) => sum + item.total_price, 0) || 0;

  if (itemsLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Smart Bundle Suggestions based on win rates */}
      {dealAmount && dealAmount > 0 && (
        <SmartBundleSuggestions
          dealAmount={dealAmount}
          onSelectBundle={(bundleId) => {
            setSelectedBundleId(bundleId);
            setAddMode('bundle');
            setShowForm(true);
          }}
        />
      )}

      {/* AI Bundle Recommendations */}
      <AIBundleRecommendations 
        dealId={dealId}
        dealAmount={dealAmount}
        accountId={accountId}
        onSelectBundle={(bundleId) => {
          setSelectedBundleId(bundleId);
          setAddMode('bundle');
          setShowForm(true);
        }}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Line Items
          </CardTitle>
          <div className="flex items-center gap-2">
            <QuickBundleCreateDialog 
              onBundleCreated={(bundleId) => {
                setSelectedBundleId(bundleId);
                setAddMode('bundle');
                setShowForm(true);
              }}
            />
            {!showForm && (
              <Button size="sm" onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
        {/* Add Item Form */}
        {showForm && (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
            <Tabs value={addMode} onValueChange={(v) => setAddMode(v as 'product' | 'bundle')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="product" className="gap-2">
                  <Package className="h-4 w-4" />
                  Product
                </TabsTrigger>
                <TabsTrigger value="bundle" className="gap-2">
                  <Layers className="h-4 w-4" />
                  Bundle
                </TabsTrigger>
              </TabsList>

              <TabsContent value="product" className="mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Product</Label>
                    <Select value={selectedProductId || "custom"} onValueChange={(v) => setSelectedProductId(v === "custom" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a product or add custom" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Custom Item</SelectItem>
                        {products?.filter(p => p.is_active).map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name} - {formatCurrency(product.base_price, product.currency)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {!selectedProductId && (
                    <div className="col-span-2">
                      <Label>Item Name</Label>
                      <Input
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder="Enter item name"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                    />
                  </div>

                  <div>
                    <Label>Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={customPrice || (selectedProduct?.base_price.toString() ?? '')}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      placeholder={selectedProduct ? `${selectedProduct.base_price}` : '0.00'}
                    />
                  </div>

                  <div>
                    <Label>Discount %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddItem} disabled={createLineItem.isPending}>
                    {createLineItem.isPending ? 'Adding...' : 'Add Item'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="bundle" className="mt-4">
                <div className="space-y-4">
                  <div>
                    <Label>Select Bundle</Label>
                    <Select value={selectedBundleId} onValueChange={setSelectedBundleId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a bundle to add" />
                      </SelectTrigger>
                      <SelectContent>
                        {bundles?.filter(b => b.is_active).map((bundle) => {
                          const savings = calculateBundleSavings(bundle);
                          return (
                            <SelectItem key={bundle.id} value={bundle.id}>
                              <div className="flex items-center gap-2">
                                <span>{bundle.name}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {formatCurrency(bundle.bundle_price, bundle.currency)}
                                </Badge>
                                {savings.savingsPercent > 0 && (
                                  <Badge variant="outline" className="text-xs text-green-600">
                                    Save {savings.savingsPercent.toFixed(0)}%
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedBundle && (
                    <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Bundle Details</span>
                        <Badge variant="outline">{selectedBundle.items?.length || 0} items</Badge>
                      </div>
                      {selectedBundle.description && (
                        <p className="text-sm text-muted-foreground">{selectedBundle.description}</p>
                      )}
                      <div className="space-y-1">
                        {selectedBundle.items?.map((item) => (
                          <div key={item.id} className="flex items-center justify-between text-sm">
                            <span>{item.product?.name || 'Unknown'} × {item.quantity}</span>
                            <span className="text-muted-foreground">
                              {formatCurrency((item.product?.base_price || 0) * item.quantity)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="pt-2 border-t flex justify-between items-center">
                        <span className="text-sm font-medium">Bundle Price</span>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const savings = calculateBundleSavings(selectedBundle);
                            return (
                              <>
                                {savings.savingsPercent > 0 && (
                                  <span className="text-sm line-through text-muted-foreground">
                                    {formatCurrency(savings.originalPrice)}
                                  </span>
                                )}
                                <span className="font-bold text-green-600">
                                  {formatCurrency(selectedBundle.bundle_price)}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAddBundle} 
                    disabled={!selectedBundleId || isAddingBundle}
                  >
                    {isAddingBundle ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Layers className="h-4 w-4 mr-2" />
                        Add Bundle
                      </>
                    )}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Line Items Table */}
        {lineItems && lineItems.length > 0 ? (
          <>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Discount</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((item) => (
                    <TableRow key={item.id} className="group">
                      <TableCell>
                        <div className="font-medium">{item.name}</div>
                        {item.ai_suggested_price && (
                          <div className="flex items-center gap-1 mt-1">
                            <Badge variant="outline" className="text-xs gap-1">
                              <Sparkles className="h-3 w-3" />
                              AI: {formatCurrency(item.ai_suggested_price)}
                            </Badge>
                          </div>
                        )}
                        {item.ai_price_reasoning && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {item.ai_price_reasoning}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.unit_price)}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.discount_percent > 0 ? `${item.discount_percent}%` : '—'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.total_price)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleAIPricing(item)}
                            disabled={aiLoading}
                            title="Get AI pricing suggestion"
                          >
                            {aiLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4 text-primary" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(item.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-end">
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Total Value</div>
                <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
              </div>
            </div>
          </>
        ) : (
          !showForm && (
            <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
              No line items added yet
            </div>
          )
        )}
      </CardContent>
      </Card>
    </div>
  );
}
